import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import {
  Avatar,
  Button,
  Checkbox,
  Empty,
  Flex,
  message,
  Modal,
  Result,
  Tooltip,
  Typography,
} from 'antd';
import { isAxiosError } from 'axios';
import { ArrowLeft, Plus, RotateCw, Settings, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';

import {
  getListCustomFieldsQueryKey,
  useArchiveCustomField,
  useBulkArchiveCustomFields,
  useListCustomFields,
} from '../../../../api';
import { usePermissions } from '../../../../modules/account/hooks';
import {
  CreateCustomFieldDrawer,
  EditCustomFieldDrawer,
} from '../../../../modules/custom-field/components/custom-field-drawer';
import { CustomFieldIcon } from '../../../../modules/custom-field/components/custom-field-icon';
import {
  customFieldRecordTypeLabels,
  customFieldTypeLabels,
} from '../../../../modules/custom-field/constants';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { Record } from '../../../../ui/components/record';

import type { ListCustomFieldsResponseDtoItemsItem } from '../../../../api/generated/model';

const SettingsCustomFieldsRoute = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const { can } = usePermissions();
  const organizationId = selectedOrganization.id;
  const canManage = can('settings', 'manageProfile');

  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editingField, setEditingField] = useState<ListCustomFieldsResponseDtoItemsItem | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectorHoverId, setSelectorHoverId] = useState<string | null>(null);

  const { data, error, status, refetch } = useListCustomFields(organizationId);
  const fields = data?.data?.items;

  const { mutateAsync: deleteFieldAsync, isPending: isDeleting } = useArchiveCustomField({
    mutation: {
      onSuccess: () => {
        message.success('Deleted');
        queryClient.invalidateQueries({
          queryKey: getListCustomFieldsQueryKey(organizationId),
        });
      },
      onError: () => {
        message.error('Failed to delete');
      },
    },
  });

  const { mutateAsync: bulkDeleteAsync } = useBulkArchiveCustomFields({
    mutation: {
      onSuccess: () => {
        message.success(`Deleted ${selectedIds.size} custom field(s)`);
        setSelectedIds(new Set());
        queryClient.invalidateQueries({ queryKey: getListCustomFieldsQueryKey(organizationId) });
      },
      onError: () => message.error('Failed to delete custom fields'),
    },
  });

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (!fields) return;
    setSelectedIds((prev) =>
      prev.size === fields.length ? new Set() : new Set(fields.map((f) => f.id)),
    );
  }, [fields]);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;

    Modal.confirm({
      title: `Delete ${selectedIds.size} custom field(s)`,
      content: 'Are you sure you want to delete the selected fields? This action cannot be undone.',
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true, 'data-testid': 'confirm' },
      onOk: () =>
        bulkDeleteAsync({
          organizationId,
          data: { customFieldIds: Array.from(selectedIds) },
        }),
    });
  }, [bulkDeleteAsync, organizationId, selectedIds]);

  const renderField = (field: ListCustomFieldsResponseDtoItemsItem) => ({
    key: field.id,
    reactNode: (
      <Record
        left={
          canManage ? (
            <Flex
              data-testid="custom-field-select"
              align="center"
              justify="center"
              style={{ width: 34, height: 34 }}
              onMouseEnter={() => setSelectorHoverId(field.id)}
              onMouseLeave={() => setSelectorHoverId(null)}
            >
              {selectedIds.has(field.id) || selectorHoverId === field.id ? (
                <Checkbox
                  checked={selectedIds.has(field.id)}
                  onChange={() => toggleSelection(field.id)}
                />
              ) : (
                <Avatar
                  size={34}
                  shape="square"
                  icon={
                    <CustomFieldIcon
                      type={field.type}
                      style={{ color: 'var(--ant-color-text-tertiary)' }}
                    />
                  }
                  style={{ backgroundColor: 'var(--ant-color-fill-tertiary)' }}
                />
              )}
            </Flex>
          ) : (
            <Avatar
              size={34}
              shape="square"
              icon={
                <CustomFieldIcon
                  type={field.type}
                  style={{ color: 'var(--ant-color-text-tertiary)' }}
                />
              }
              style={{ backgroundColor: 'var(--ant-color-fill-tertiary)' }}
            />
          )
        }
        title={field.name}
        description={customFieldTypeLabels[field.type]}
        right={
          canManage ? (
            <Flex>
              <Tooltip title="Configure">
                <Button
                  data-testid="custom-field-configure"
                  color="default"
                  variant="text"
                  icon={<Settings size="1em" />}
                  onClick={() => setEditingField(field)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  data-testid="custom-field-delete"
                  color="danger"
                  variant="text"
                  danger
                  icon={<Trash2 size="1em" />}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Delete custom field',
                      content:
                        'Are you sure you want to delete this field? This action cannot be undone.',
                      okText: 'Delete',
                      cancelText: 'Cancel',
                      okButtonProps: {
                        danger: true,
                        loading: isDeleting,
                        'data-testid': 'confirm',
                      },
                      onOk: () =>
                        deleteFieldAsync({
                          organizationId,
                          customFieldId: field.id,
                        }),
                    });
                  }}
                />
              </Tooltip>
            </Flex>
          ) : null
        }
      />
    ),
  });

  const content = {
    pending: <Loader />,
    error:
      isAxiosError(error) && error.response?.status === 403 ? (
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view or edit this page. Please contact your administrator."
          extra={
            <Button icon={<ArrowLeft size="1em" />} onClick={() => window.history.back()}>
              Go back
            </Button>
          }
        />
      ) : (
        <Result
          status="error"
          title="Failed to load!"
          subTitle="Please try again. If this keeps happening, check your connection or contact support."
          extra={
            <Button icon={<RotateCw size="1em" />} onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      ),
    success: !fields?.length ? (
      <Empty description="No custom fields found" />
    ) : (
      <>
        {canManage && (
          <Flex style={{ paddingBottom: 12 }}>
            <Checkbox
              indeterminate={selectedIds.size > 0 && selectedIds.size < fields.length}
              checked={selectedIds.size === fields.length}
              onChange={toggleSelectAll}
            >
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {selectedIds.size > 0 ? (
                  <>
                    <span className="font-mono">{selectedIds.size}</span> selected
                  </>
                ) : (
                  'Select all'
                )}
              </Typography.Text>
            </Checkbox>
          </Flex>
        )}
        <Flex vertical gap={20}>
          {(['lead', 'contact', 'deal'] as const).map((rt) => {
            const groupFields = fields.filter((field) => field.recordType === rt);
            if (groupFields.length === 0) {
              return null;
            }
            return (
              <Flex vertical gap={8} key={rt} data-testid={`custom-fields-${rt}`}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {customFieldRecordTypeLabels[rt]}
                </Typography.Text>
                <List items={groupFields} renderItem={renderField} />
              </Flex>
            );
          })}
        </Flex>
      </>
    ),
  };

  return (
    <SettingsLayoutContent
      title="Custom Fields"
      description="Define custom fields for your records."
      action={
        canManage ? (
          <Flex gap={8}>
            {selectedIds.size > 0 && (
              <Button
                data-testid="custom-fields-delete"
                variant="filled"
                color="danger"
                size="large"
                icon={<Trash2 size="1em" />}
                onClick={handleBulkDelete}
              />
            )}
            <Button
              data-testid="custom-field-create"
              color="default"
              variant="filled"
              size="large"
              icon={<Plus size="1em" />}
              onClick={() => setCreateDrawerOpen(true)}
            />
          </Flex>
        ) : undefined
      }
    >
      {content[status]}

      {createDrawerOpen ? (
        <CreateCustomFieldDrawer
          open
          organizationId={organizationId}
          onClose={() => {
            setCreateDrawerOpen(false);
          }}
        />
      ) : null}

      {editingField ? (
        <EditCustomFieldDrawer
          open
          customField={editingField}
          organizationId={organizationId}
          onClose={() => {
            setEditingField(null);
          }}
        />
      ) : null}
    </SettingsLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/custom-fields')({
  component: SettingsCustomFieldsRoute,
});
